import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { SnackType } from '@/features/snacks/enums/SnackType';
import BenefitEvents from '@/domains/benefits/events';
import {
  getBenefitSectionDetails,
  updateClaimSetClaim,
} from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';
import { BenefitTransactionStatuses } from '../enums/BenefitTransactionStatuses';
import {
  createBenefit,
  duplicateBenefitTransaction,
  getBenefitTransactionDetailsError,
  getBenefitTransactionDetailsSuccess,
  getBenefitTransactionsDetails,
  getBenefitValue,
  getBenefitValueSuccess,
  incrementBenefitTransactionsPage,
  startLoadingBenefitTransactionsDetails,
} from '../store/benefitsSlice';
import {
  selectBenefitTransactionsDetailsPagination,
  selectBenefitTransactionsDetailsSorting,
  selectHideCompleteBenefitTransactions,
} from '../store/selectors/benefitsSliceSelectors';

function* createBenefitSaga({ payload: { claimId } }: PayloadAction<{ claimId: number }>) {
  try {
    const { data } = yield call(
      HttpService.post,
      `examiner/benefit`,
      {
        claimId,
      },
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    SnacksService.add({
      type: SnackType.Success,
      title: `Benefit ${data.benefitNumber} was successfully created!`,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(updateClaimSetClaim(String(claimId)));
    yield put(getBenefitSectionDetails(String(claimId)));

    EventService.dispatch(new BenefitEvents.BenefitCreated({ claimId }));
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to generate a benefit',
      content: 'Please try again later',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    console.error(error);
  }
}

function* getBenefitValueSaga({ payload: { claimId } }: PayloadAction<{ claimId: string }>) {
  try {
    const { data } = yield call(
      HttpService.get,
      `examiner/benefit/eligibility-to-create?claimId=${claimId}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    yield put(getBenefitValueSuccess({ addBenefitValue: data }));
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to get add benefit value',
      content: 'Please try again later',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    console.error(error);
  }
}

function* getBenefitTransactionsDetailsSaga({ type, payload }: PayloadAction<{ benefitId: string }>) {
  try {
    yield put(startLoadingBenefitTransactionsDetails({ benefitId: payload.benefitId }));

    const TRANSACTIONS_INCLUDES = [`${EdsEntityCodes.PaymentMethod}.${EdsEntityCodes.Address}`].join(',');

    const pagination = yield select(selectBenefitTransactionsDetailsPagination(payload.benefitId));
    const sorting = yield select(selectBenefitTransactionsDetailsSorting(payload.benefitId));
    const hideCompleteStatus = yield select(selectHideCompleteBenefitTransactions(payload.benefitId));

    const filters = [
      { propertyName: EdsFilterEntities.BenefitId, values: [payload.benefitId] },
      ...(hideCompleteStatus
        ? [
            {
              propertyName: EdsFilterEntities.TransactionStatus,
              values: [
                BenefitTransactionStatuses.Pending,
                BenefitTransactionStatuses.ReadyForPayment,
                BenefitTransactionStatuses.Active,
              ],
            },
          ]
        : []),
    ];
    const requestUrl = `/filter/${EdsEntityCodes.Transaction}?${QueryParamsService.constructSearchParamsFromRecord({
      page: pagination.page,
      perPage: pagination.perPage,
      sortBy: edsToEntityFormat(sorting.sortBy),
      sortDirection: sorting.sortDirection,
      includes: TRANSACTIONS_INCLUDES,
    })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);

    // commented due to the pollution of console, uncomment if needed
    // EdsDebugService.logResponse(EdsEntityCodes.Transaction, {
    //   rawResponse: data,
    //   normalizedResponse,
    // });

    yield put(
      getBenefitTransactionDetailsSuccess({
        benefitId: payload.benefitId,
        shouldRewrite: type !== incrementBenefitTransactionsPage.type,
        data: {
          listings: normalizedResponse,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
          sorting,
          hideCompleteTransactions: hideCompleteStatus,
        },
      }),
    );
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Transactions fetching error',
      content: 'Unable to fetch transactions. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    yield put(getBenefitTransactionDetailsError({ benefitId: payload.benefitId }));
  }
}

function* duplicateBenefitTransactionSaga({
  payload: { claimId, transactionId, benefitId },
}: PayloadAction<{ claimId: string | number; transactionId: string | number; benefitId: string | number }>) {
  try {
    yield call(HttpService.post, `/${EdsEntityCodes.Transaction}/duplicate`, { transactionId });

    yield put(getBenefitTransactionsDetails({ benefitId }));
    yield put(getBenefitSectionDetails(String(claimId)));
  } catch (error) {
    console.error(error);
  }
}

export function* benefitsWatcher() {
  yield takeLatest([createBenefit.type], createBenefitSaga);
  yield takeLatest([getBenefitValue.type], getBenefitValueSaga);
  yield takeEvery(getBenefitTransactionsDetails.type, getBenefitTransactionsDetailsSaga);
  yield takeLatest(duplicateBenefitTransaction.type, duplicateBenefitTransactionSaga);
}
